import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Collection", href: "#gallery" },
  { label: "Philosophy", href: "#about" },
  { label: "Craft", href: "#features" },
  { label: "Voices", href: "#testimonials" },
  { label: "Journal", href: "#newsletter" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, delay: 1.9, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0a0f1d]/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between py-5 md:py-6">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          <span className="text-[10px] tracking-[0.4em] text-[#D4AF37] uppercase">
            Est. 2025 · NSW
          </span>
        </a>

        <a
          href="#top"
          className="absolute left-1/2 -translate-x-1/2 flex items-center"
          data-testid="nav-wordmark"
          aria-label="HueBleu"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_scroll-elegance-1/artifacts/3iupz0o1_logoHB_bgremoved.png"
            alt="HueBleu"
            className="h-16 md:h-20 lg:h-24 w-auto object-contain drop-shadow-[0_2px_18px_rgba(212,175,55,0.35)]"
          />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="gold-link text-[12px] tracking-[0.22em] uppercase"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          className="md:hidden text-white"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="md:hidden overflow-hidden bg-[#0a0f1d]/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-[0.25em] uppercase text-white/80 hover:text-[#D4AF37] transition-colors"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
